import * as Sentry from '@sentry/browser';
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';

import makeApiRequest from '../../utils/makeApiRequest';

import { API_ENDPOINTS } from '../../constants';
import Attribute from './Attribute';
import Button from './Button';
import Header from './Header';
import NumberList from './NumberList';
import SectionDivider from './SectionDivider';
import Skeleton from './Skeleton';
import Toggle from './Toggle';

const DeviceTypes = {
  IPHONE: 'iPhone',
  ANDROID: 'Android',
};

const StepInstall = ({ onInstallConfirm, onNumberAllocation, customerId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [simInfo, setSimInfo] = useState(null);
  const [deviceType, setDeviceType] = useState(DeviceTypes.IPHONE);

  useEffect(() => {
    makeApiRequest(API_ENDPOINTS.ACTIVATION_INSTALL, {
      CustomerId: customerId,
    })
      .then((responseData) => {
        console.log(responseData.simInfo);
        setSimInfo(responseData.simInfo);
        onNumberAllocation(responseData.MDN);
        setFetching(false);
      })
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
        setShowError(true);
      })
      .finally(() => {
        setFetching(false);
      });
  }, []);

  const handleConfirm = (e) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      onInstallConfirm();
    }, 1000);
  };

  if (fetching) {
    return html`<${Skeleton} />`;
  }

  if (showError) {
    return html` <${Header}
      title="Sorry! We couldn't get your eSim right now."
      description="Please try again or get in touch with our support team."
    />`;
  }

  return html`
    <${Header} title="Install Your eSim" description="You have two easy options to check your device.">
      <${Toggle} name="deviceType" options=${Object.values(DeviceTypes)} onChange=${setDeviceType} />
    </${Header}>
    <form class="flex flex-col items-center">
      <div class="flex flex-col lg:flex-row gap-6 lg:gap-12 mb-8">
        <div class="flex flex-col items-center w-full lg:w-1/2">
          <h3 class="h4 mb-2 normal-case text-center">Scan The QR Code</h3>
          <div class="rounded-3xl p-4 bg-white">
            <img src=${simInfo.qrCode} alt="QR Code" class="w-44 h-44" />
          </div>
          <h4 class="flex h5 mb-2 mt-4">Follow These Steps</h4>
          <div class="w-full max-w-52">
            <${NumberList}
              items=${[
                'Scan the QR code using the phone your Mingle Mobile service will be on.',
                'Follow the setup steps.',
              ]}
            />
          </div>
          <div class="block lg:hidden mt-4">
            <${Button} text="I've Installed My eSIM" onClick=${handleConfirm} loading=${isLoading} />
          </div>
        </div>
        <${SectionDivider} />
        <div class="flex flex-col w-full lg:w-1/2">
          <h3 class="flex h4 mb-2">Manual Entry</h3>
          <p class="text-sm mb-6">Don’t want to use our QR code? Don’t worry, you can add the plan manually.</p>
          ${
            deviceType === DeviceTypes.IPHONE &&
            html`
              <${NumberList}
                items=${[
                  'Go to Settings → Cellular → Add Cellular Plan → Enter Details Manually',
                  'Use the following attributes.',
                ]}
              />
            `
          }
          ${
            deviceType === DeviceTypes.ANDROID &&
            html`
              <${NumberList}
                items=${[
                  'Go to Network & Internet in your device Settings.',
                  'Tap on Add Mobile Network (the + icon)',
                  'Select Download a SIM instead.',
                  'Follow directions on your device till you reach the scanner.',
                  'Tap on the Need help button below the scanner.',
                  'Proceed with Enter it manually and paste the QR code there.',
                ]}
              />
            `
          }
          <div class="w-full mt-6">
            <${Attribute} name="SM-PD+ADDRESS" value=${simInfo.smdpAddress} />
            <${Attribute} name="Activation Code" value=${simInfo.activationCode} />
          </div>

          <div class="flex justify-center lg:hidden mt-3">
            <${Button} text="I've Installed My eSIM" onClick=${handleConfirm} loading=${isLoading} />
          </div>
        </div>
      </div>
      <div class="hidden lg:flex justify-center">
        <${Button} text="I've Installed My eSIM" onClick=${handleConfirm} loading=${isLoading} />
      </div>
    </form>
  `;
};

export default StepInstall;
